/**
 * The one HTTP client. Every request the SPA makes goes through here — no raw
 * `fetch` in components, no hardcoded hosts (rules/web.md).
 *
 * The base URL is empty in production: CloudFront serves the SPA and the API
 * from one origin, so `/api/...` is always the right path. In development Vite
 * proxies it to the local API.
 */

import { nl } from "../copy/nl.js";

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "";

/**
 * The Phase-0 admin gate: one shared toegangscode, kept for the tab's
 * lifetime only (sessionStorage — surviving a browser restart would make a
 * shared machine an open admin). F-9 (Google OIDC) replaces this.
 */
const CODE_KEY = "bivak-beheercode";

export function zetBeheerCode(code: string): void {
  sessionStorage.setItem(CODE_KEY, code);
}

export function heeftBeheerCode(): boolean {
  return sessionStorage.getItem(CODE_KEY) !== null;
}

export function wisBeheerCode(): void {
  sessionStorage.removeItem(CODE_KEY);
}

function beheerHeaders(path: string): Record<string, string> {
  if (!path.startsWith("/api/admin/")) return {};
  const code = sessionStorage.getItem(CODE_KEY);
  return code ? { authorization: `Bearer ${code}` } : {};
}

/** `GET /api/availability` — dates as YYYY-MM-DD strings, exactly as on the wire. */
export type AvailabilityResponse = {
  from: string;
  to: string;
  resources: { id: string; name: string }[];
  spaces: {
    id: string;
    name: string;
    description: string | null;
    maxCapacity: number;
    minCapacity: number;
    resourceIds: string[];
    unavailable: { from: string; to: string }[];
  }[];
  occupancy: {
    resourceId: string;
    from: string;
    to: string;
    kind: "rental" | "blackout" | "maintenance";
  }[];
};

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

async function get<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, { headers: beheerHeaders(path) });
  if (!response.ok) {
    // The API's error envelope carries a Dutch message that is safe to show.
    const body = (await response.json().catch(() => undefined)) as
      | { error?: { code?: string; message?: string } }
      | undefined;
    throw new ApiError(
      body?.error?.code ?? "unknown",
      // The API's own message is Dutch and safe to show; this is the fallback
      // for when the response carried no readable body at all.
      body?.error?.message ?? nl.algemeen.fout,
      response.status,
    );
  }
  return (await response.json()) as T;
}

/** One row of `GET /api/admin/bookings`. Contact data is deliberately absent. */
export type AdminBookingRow = {
  id: string;
  partyName: string;
  isSystemParty: boolean;
  purpose: string | null;
  status: string;
  stayType: "overnight" | "day";
  arrivalDate: string;
  departureDate: string;
  spaceName: string;
  personsBooked: number | null;
  totalCents: number | null;
  optionExpiresAt: string | null;
  /** Settlement balance: positive was refunded, negative is still owed. */
  balanceCents: number | null;
};

export type AdminBookingDetail = AdminBookingRow & {
  arrivalTime: string | null;
  departureTime: string | null;
  createdAt: string;
  /** The renter's own words from the form (0.22) — detail only, never the list. */
  renterNote: string | null;
  renterReference: string | null;
  party: {
    name: string;
    category: string | null;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
  };
  headcounts: { kind: string; total: number; under31: number | null }[];
  lines: {
    id: string;
    description: string;
    quantity: number;
    unitCents: number;
    amountCents: number;
  }[];
  timeline: {
    seq: number;
    label: string;
    at: string;
    actorName: string | null;
    note: string | null;
  }[];
  deposit: {
    status: string;
    requestedCents: number;
    receivedCents: number;
    withheldCents: number;
    withheldReason: string | null;
    refundedCents: number;
  } | null;
};

export function fetchAdminBookings(): Promise<{ bookings: AdminBookingRow[] }> {
  return get<{ bookings: AdminBookingRow[] }>("/api/admin/bookings");
}

export function fetchAdminBooking(id: string): Promise<AdminBookingDetail> {
  return get<AdminBookingDetail>(`/api/admin/bookings/${encodeURIComponent(id)}`);
}

export type QuoteResponse = {
  verhuurtype: "dag" | "weekend" | "kamp";
  lines: { kind: string; quantity: number; unitCents: number; amountCents: number }[];
  totalCents: number;
};

export function fetchQuote(van: string, tot: string, personen: number): Promise<QuoteResponse> {
  const query = new URLSearchParams({ van, tot, personen: String(personen) });
  return get<QuoteResponse>(`/api/quote?${query.toString()}`);
}

export type NewBooking = {
  boekingId: string;
  van: string;
  tot: string;
  /** Only for overnight stays; a dag books without a headcount. */
  personen?: number;
  groepsnaam: string;
  contactNaam: string;
  email: string;
  telefoon: string;
  doel?: string;
  opmerking?: string;
  referentie?: string;
};

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: { "content-type": "application/json", ...beheerHeaders(path) },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => undefined)) as
      | { error?: { code?: string; message?: string } }
      | undefined;
    throw new ApiError(
      payload?.error?.code ?? "unknown",
      payload?.error?.message ?? nl.algemeen.fout,
      response.status,
    );
  }
  return (await response.json()) as T;
}

/** `totalCents` is absent on the idempotent retry — the first submission won. */
export function createBooking(body: NewBooking): Promise<{ id: string; totalCents?: number }> {
  return post<{ id: string; totalCents?: number }>("/api/bookings", body);
}

export type AfrekeningResponse = {
  status: string;
  stayType: "overnight" | "day";
  arrivalDate: string;
  departureDate: string;
  readings: {
    meter: "electricity" | "gas" | "water";
    moment: "arrival" | "departure";
    value: number;
  }[];
  prijzen: {
    meter: "electricity" | "gas" | "water";
    unit: string;
    priceCents: number;
    validFrom: string;
  }[];
  deposit: { receivedCents: number } | null;
  settlement: {
    consumptionCents: number;
    damageCents: number;
    depositAppliedCents: number;
    balanceCents: number;
    settledAt: string;
  } | null;
};

export function fetchAfrekening(id: string): Promise<AfrekeningResponse> {
  return get<AfrekeningResponse>(`/api/admin/bookings/${encodeURIComponent(id)}/afrekening`);
}

export type NewLezing = {
  meter: "electricity" | "gas" | "water";
  moment: "arrival" | "departure";
  value: number;
};

async function send<T>(
  method: "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      ...(body === undefined ? {} : { "content-type": "application/json" }),
      ...beheerHeaders(path),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => undefined)) as
      | { error?: { code?: string; message?: string } }
      | undefined;
    throw new ApiError(
      payload?.error?.code ?? "unknown",
      payload?.error?.message ?? nl.algemeen.fout,
      response.status,
    );
  }
  return (await response.json()) as T;
}

export function saveLezingen(id: string, lezingen: NewLezing[]): Promise<{ saved: number }> {
  return send<{ saved: number }>("PUT", `/api/admin/bookings/${encodeURIComponent(id)}/lezingen`, {
    lezingen,
  });
}

/** Reopen a settlement: settled → stayed, the settlement voided (0.2b). */
export function reopenAfrekening(id: string): Promise<{ status: string }> {
  return send<{ status: string }>(
    "DELETE",
    `/api/admin/bookings/${encodeURIComponent(id)}/afrekening`,
  );
}

export type GegevensWijziging = {
  groepsnaam?: string;
  contactNaam?: string;
  contactEmail?: string;
  contactTelefoon?: string;
  doel?: string | null;
};

export function patchGegevens(
  id: string,
  wijziging: GegevensWijziging,
): Promise<{ aangepast: string[] }> {
  return send<{ aangepast: string[] }>(
    "PATCH",
    `/api/admin/bookings/${encodeURIComponent(id)}/gegevens`,
    wijziging,
  );
}

export type SettleBody = {
  schadeCents: number;
  /** Actual attendance (0.13b): the subsidy is computed from it, required. */
  actualPersonen: number;
  actualOnder31: number;
};

/** `balanceCents` is absent on the idempotent retry — the first settle won. */
export function settleBooking(
  id: string,
  body: SettleBody,
): Promise<{ status: string; balanceCents?: number }> {
  return post<{ status: string; balanceCents?: number }>(
    `/api/admin/bookings/${encodeURIComponent(id)}/afrekening`,
    body,
  );
}

export type AdminBlokkade = {
  from: string;
  to: string;
  kind: "blackout" | "maintenance";
  reason: string | null;
};

export function fetchAdminBlokkades(): Promise<{ blokkades: AdminBlokkade[] }> {
  return get<{ blokkades: AdminBlokkade[] }>("/api/admin/bookings/blokkades");
}

/** `POST /api/admin/demo-reset` — wipe every change, restore the seed (0.20). */
export function herstelDemo(): Promise<{ hersteld: boolean }> {
  return post<{ hersteld: boolean }>("/api/admin/demo-reset", {});
}

/** `POST .../transitions` — the state machine decides; 409 means look again. */
export function postBookingTransition(id: string, transition: string): Promise<{ status: string }> {
  return post<{ status: string }>(`/api/admin/bookings/${encodeURIComponent(id)}/transitions`, {
    transition,
  });
}

/** The tariffs in force today (0.25) — the same rows the quote prices from. */
export type Tarief = {
  verhuurtype: "dag" | "weekend" | "kamp";
  perPersonPerNightCents: number | null;
  perDayCents: number | null;
  minimumCents: number;
};

export function fetchTarieven(): Promise<{ tarieven: Tarief[] }> {
  return get("/api/tarieven");
}

/** The beheer view carries the waarborg; the public endpoint strips it (Q7). */
export type BeheerTarief = Tarief & { waarborgCents: number };

type EnergieVersie = { meter: string; unit: string; priceCents: number; validFrom: string };

export type EnergieTarief = EnergieVersie & {
  historiek: EnergieVersie[];
  gepland: EnergieVersie[];
};

export function fetchAdminTarieven(): Promise<{
  verhuur: BeheerTarief[];
  komend: (BeheerTarief & { vanaf?: string })[];
  energie: EnergieTarief[];
}> {
  return get("/api/admin/tarieven");
}

export type NieuwVerhuurtarief = BeheerTarief & { vanaf: string };

export function publiceerVerhuurtarief(body: NieuwVerhuurtarief): Promise<{ ok: boolean }> {
  return post("/api/admin/tarieven/verhuur", body);
}

export function publiceerEnergieprijs(body: {
  meter: string;
  priceCents: number;
  vanaf: string;
}): Promise<{ ok: boolean }> {
  return post("/api/admin/tarieven/energie", body);
}

/** Drives the public demo banner — `demo` disappears with the go-live gate. */
export function fetchHealth(): Promise<{ status: string; version: string; demo: boolean }> {
  return get("/api/health");
}

export function fetchAvailability(from?: string, to?: string): Promise<AvailabilityResponse> {
  const query = new URLSearchParams();
  if (from) query.set("from", from);
  if (to) query.set("to", to);
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  return get<AvailabilityResponse>(`/api/availability${suffix}`);
}
